import { useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { RpiButton, RpiDrawerNumberInput, RpiDrawerSelectInput, RpiDrawerTextInput } from "@rpi/openapi-core";
import React from "react";
import { useParams } from "react-router-dom";
import { createDrawerOverlay, RpiDrawer, RpiDrawerForm } from "../../rpi-core/drawer/RpiDrawer";
import { formatCurrency } from "../../utils/misc.util";
import { shouldRetry } from "../../utils/query.util";

type Order = GraphQLQueryData<GraphQL.OrderQuery>;

function toSafeNumber(value: number | null | undefined): number {
  return value ?? 0;
}

function calculateMaxRefundPercentage(remainingRefund: number, orderTotal: number): number {
  return (remainingRefund / orderTotal) * 100;
}

function calculatePercentageRefundAmount(orderTotal: number, percentage: number): number {
  return Math.round(orderTotal * (percentage || 0)) / 100;
}

function parseFormNumber(value: string | number): number {
  return typeof value === "string" ? Number.parseFloat(value) : value;
}

interface RefundStrategyOption {
  label: string;
  value: GraphQL.BraintreeRefundStrategy;
}

function buildRefundStrategyOptions(
  order: Order,
  shippingTotal: number,
  remainingRefund: number
): RefundStrategyOption[] {
  const options: RefundStrategyOption[] = [
    { label: "Partial refund", value: GraphQL.BraintreeRefundStrategy.OrderPartialValue },
    { label: "Partial percentage refund", value: GraphQL.BraintreeRefundStrategy.OrderPercentageZeroToOne },
  ];

  if (order.canDoFullRefund) {
    options.push({ label: "Full refund", value: GraphQL.BraintreeRefundStrategy.OrderFullValue });
  }

  if (shippingTotal < remainingRefund) {
    options.push({ label: "Shipping only", value: GraphQL.BraintreeRefundStrategy.ShippingOnly });
  }

  return options;
}

function getDefaultRefundStrategy(order: Order): GraphQL.BraintreeRefundStrategy {
  return order.canDoFullRefund
    ? GraphQL.BraintreeRefundStrategy.OrderFullValue
    : GraphQL.BraintreeRefundStrategy.OrderPartialValue;
}

function getStrategyValue(
  strategy: GraphQL.BraintreeRefundStrategy,
  partialValue: number,
  percentage: number
): number | undefined {
  if (strategy === GraphQL.BraintreeRefundStrategy.OrderPartialValue) {
    return parseFormNumber(partialValue);
  }
  if (strategy === GraphQL.BraintreeRefundStrategy.OrderPercentageZeroToOne) {
    return parseFormNumber(percentage) / 100;
  }
  return undefined;
}

function isPartialRefundStrategy(strategy: GraphQL.BraintreeRefundStrategy): boolean {
  return strategy === GraphQL.BraintreeRefundStrategy.OrderPartialValue;
}

function isPercentageRefundStrategy(strategy: GraphQL.BraintreeRefundStrategy): boolean {
  return strategy === GraphQL.BraintreeRefundStrategy.OrderPercentageZeroToOne;
}

function calculateRefundAmount(
  strategy: GraphQL.BraintreeRefundStrategy,
  orderTotal: number,
  remainingRefund: number,
  shippingTotal: number,
  formValues: { partialValue: number; percentage: number }
): number {
  switch (strategy) {
    case GraphQL.BraintreeRefundStrategy.OrderFullValue:
      return remainingRefund;
    case GraphQL.BraintreeRefundStrategy.OrderPartialValue:
      return formValues.partialValue;
    case GraphQL.BraintreeRefundStrategy.OrderPercentageZeroToOne:
      return calculatePercentageRefundAmount(orderTotal, formValues.percentage);
    case GraphQL.BraintreeRefundStrategy.ShippingOnly:
      return shippingTotal;
    default:
      return 0;
  }
}

export const OrderRefundView = ({ opened, onClose }: { opened: boolean; onClose: () => void }) => {
  const { customerPublicId, customerOrderId } = useParams();

  const [closing, setClosing] = React.useState(false);

  const handleClose = React.useCallback(
    (delay?: boolean) => {
      if (closing) return;

      if (delay) {
        setClosing(true);
        return setTimeout(() => {
          onClose();
          setClosing(false);
        }, 1000);
      }
      return onClose();
    },
    [onClose, closing]
  );

  const { data, isLoading, error } = GraphQL.useOrderQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: false,
    },
    {
      enabled: !!customerPublicId && !!customerOrderId && opened,
      refetchOnMount: true,
      retry: shouldRetry(handleClose),
      cacheTime: 0,
    }
  );
  const order = data?.orderByIdentifier;

  return (
    <RpiDrawer title="Refund order" isLoading={isLoading} error={error} opened={opened} onClose={handleClose}>
      {order && <RefundOrder order={order} onClose={() => handleClose(true)} />}
    </RpiDrawer>
  );
};

interface RefundOrderProps {
  order: Order;
  onClose: () => void;
}

const RefundOrder: React.FC<RefundOrderProps> = ({ order, onClose }) => {
  const { mutateAsync: refundOrder, reset, ...mutation } = GraphQL.useAdminRefundOrderMutation<{ message: string }>();
  const orderTotal = toSafeNumber(order.pricing?.orderTotal);
  const shippingTotal = toSafeNumber(order.pricing?.shippingTotal);
  const remainingRefund = toSafeNumber(order.remainingPossibleRefund);

  const selectOptions = buildRefundStrategyOptions(order, shippingTotal, remainingRefund);
  const [refundStrategy, setRefundStrategy] = React.useState<GraphQL.BraintreeRefundStrategy>(
    getDefaultRefundStrategy(order)
  );

  const form = useForm<{
    partialValue: number;
    percentage: number;
  }>({
    initialValues: {
      partialValue: remainingRefund,
      percentage: calculateMaxRefundPercentage(remainingRefund, orderTotal),
    },
    validate: {},
  });

  React.useEffect(() => reset, []);

  const handleSubmit = React.useCallback(
    async ({ partialValue, percentage }: typeof form.values) => {
      if (!order.customer?.publicId) {
        throw new Error("Missing customer public id.");
      }

      const strategyValue = getStrategyValue(refundStrategy, partialValue, percentage);

      await refundOrder({
        customerPublicId: order.customer.publicId,
        input: {
          refundStrategy,
          customerOrderId: order.customerOrderId,
          ...(strategyValue !== undefined && { refundStrategyValue: strategyValue }),
        },
      });

      onClose();
    },
    [refundOrder, order, onClose, refundStrategy]
  );

  const overlay = React.useMemo(
    () =>
      createDrawerOverlay({
        loading: {
          message: "Refunding order...",
          description: "Please wait, do not close this screen.",
        },
        error: {
          message: mutation.error?.message || "Failed to refund order...",
          description: "Please try again later.",
        },
        success: {
          message: "Order refunded!",
        },
      })(mutation),
    [mutation]
  );

  const refundAmount = calculateRefundAmount(refundStrategy, orderTotal, remainingRefund, shippingTotal, form.values);

  return (
    <RpiDrawerForm overlay={overlay} onSubmit={form.onSubmit(handleSubmit)}>
      <RpiDrawerTextInput disabled label="Customer Order ID" value={order.customerOrderId || ""} />
      <RpiDrawerSelectInput
        label="Strategy"
        data={selectOptions}
        value={refundStrategy}
        onChange={(value) => setRefundStrategy(value as GraphQL.BraintreeRefundStrategy)}
      />

      {isPartialRefundStrategy(refundStrategy) && (
        <RpiDrawerNumberInput
          required
          label={`Amount (${order.currency})`}
          min={0}
          max={remainingRefund}
          step="0.01"
          {...form.getInputProps("partialValue")}
        />
      )}

      {isPercentageRefundStrategy(refundStrategy) && (
        <RpiDrawerNumberInput
          required
          label="Percentage (%)"
          min={0}
          max={calculateMaxRefundPercentage(remainingRefund, orderTotal)}
          step="0.01"
          {...form.getInputProps("percentage")}
        />
      )}

      <RpiButton
        type="submit"
        variant="mellow"
        mt={24}
        width="full"
        children={`Refund ${formatCurrency(order.currency, refundAmount || 0)}`}
      />
    </RpiDrawerForm>
  );
};
